import React from 'react';
import { Typography, Tooltip } from 'antd';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const { Text } = Typography;

// Styled Component
const StyledWorkExperiences = styled.div`
    &.layout-block {
        display: flex;
        flex-direction: column;

        .work-experience-item {
            line-height: 1.5;
        }
    }

    &.layout-inline {
        display: inline;
    }

    &.layout-ordered-list {
        .work-experience-item {
            line-height: 1.5;
        }
    }
`;

/**
 * 专家/嘉宾单位任职信息组件
 * 用于统一展示 ssoMemberWorkExperiencesEntities 数据
 *
 * @param {Array} workExperiences - 单位任职经历数组
 * @param {boolean} showSerialNumber - 是否显示序号（当多个任职时）
 * @param {boolean} showJobTitle - 是否显示职称字段
 * @param {string} separator - 各字段之间的分隔符
 * @param {string} emptyText - 无数据时显示的文本
 * @param {string} layout - 布局方式：'block'（块级）、'inline'（行内）或 'orderedList'（有序列表）
 * @param {object} className - 自定义类名
 * @param {function} customFormatter - 自定义格式化函数 (item, context) => { unitName, officePostDisplay }
 * @param {object} formatterContext - 传递给 customFormatter 的上下文对象
 * @param {boolean} showTooltipStyle - 是否显示 Tooltip 视觉样式（虚线下划线和帮助光标）
 */
const WorkExperiences = ({ workExperiences, showSerialNumber = true, showJobTitle = false, separator = '', emptyText = '暂无', layout = 'block', className = '', customFormatter = null, formatterContext = null, showTooltipStyle = true }) => {
    // 如果没有数据或数据为空
    if (!workExperiences || workExperiences.length === 0) {
        return emptyText === '–' ? <Text type="secondary">&ndash;</Text> : <Text type="secondary">{emptyText}</Text>;
    }

    // 是否显示序号：只在有多个任职且开启了序号显示时才显示
    const shouldShowSerialNumber = showSerialNumber && workExperiences.length > 1;

    // 渲染单个工作经历
    const renderWorkExperience = (item, index) => {
        if (!item) return null;

        let unitNameElement = null;
        let otherContent = '';

        // 使用自定义格式化函数
        if (customFormatter && typeof customFormatter === 'function') {
            const formatted = customFormatter(item, formatterContext);
            if (!formatted) return null;

            const { unitName, officePostDisplay } = formatted;

            // 单位名称（带 Tooltip）
            if (unitName) {
                const hasOrganisation = item.memUnitId && item.organisation;
                const province = hasOrganisation ? item.organisation.province : '';
                const city = hasOrganisation ? item.organisation.city : '';
                const tooltipTitle = hasOrganisation && (province || city) ? `单位所在地：${province || ''}${city || ''}` : '';

                if (tooltipTitle) {
                    const tooltipStyle = showTooltipStyle ? { cursor: 'help', borderBottom: '1px dashed rgba(0,0,0,0.2)' } : {};

                    unitNameElement = (
                        <Tooltip title={tooltipTitle} placement="top">
                            <span style={tooltipStyle}>{unitName}</span>
                        </Tooltip>
                    );
                } else {
                    unitNameElement = <span>{unitName}</span>;
                }
            }

            otherContent = officePostDisplay || '';
        } else {
            // 使用默认格式化逻辑
            const parts = [];

            // 单位名称（带 Tooltip）
            if (item.unitName) {
                const hasOrganisation = item.memUnitId && item.organisation;
                const province = hasOrganisation ? item.organisation.province : '';
                const city = hasOrganisation ? item.organisation.city : '';
                const tooltipTitle = hasOrganisation && (province || city) ? `单位所在地：${province || ''}${city || ''}` : '';

                if (tooltipTitle) {
                    const tooltipStyle = showTooltipStyle ? { cursor: 'help', borderBottom: '1px dashed rgba(0,0,0,0.2)' } : {};

                    unitNameElement = (
                        <Tooltip title={tooltipTitle} placement="top">
                            <span style={tooltipStyle}>{item.unitName}</span>
                        </Tooltip>
                    );
                } else {
                    unitNameElement = <span>{item.unitName}</span>;
                }
            }

            // 科室/部门
            if (item.unitOffice && item.unitOffice !== 'null') {
                parts.push(item.unitOffice);
            }

            // 职务
            if (item.unitPost && item.unitPost !== 'null') {
                parts.push(item.unitPost);
            }

            // 职称（可选）
            if (showJobTitle && item.unitJobTitle && item.unitJobTitle !== 'null') {
                parts.push(item.unitJobTitle);
            }

            otherContent = parts.join(separator);
        }

        // 如果单位名称和其他字段都为空，返回null
        if (!unitNameElement && !otherContent) {
            return null;
        }

        // 构建内容：序号 + 单位名称 + 其他字段
        const serialNumber = shouldShowSerialNumber ? `${index + 1}. ` : '';

        return (
            <React.Fragment key={item.id || index}>
                {serialNumber}
                {unitNameElement}
                {unitNameElement && otherContent && separator}
                {otherContent}
            </React.Fragment>
        );
    };

    // 过滤掉空的工作经历
    const validExperiences = workExperiences.map((item, index) => renderWorkExperience(item, index)).filter(item => item !== null);

    // 如果过滤后没有有效数据
    if (validExperiences.length === 0) {
        return emptyText === '–' ? <Text type="secondary">&ndash;</Text> : <Text type="secondary">{emptyText}</Text>;
    }

    // 有序列表布局
    if (layout === 'orderedList') {
        return (
            <StyledWorkExperiences as="ol" className={`layout-ordered-list ${className}`}>
                {validExperiences.map((item, index) => (
                    <li key={index} className="work-experience-item">
                        {item}
                    </li>
                ))}
            </StyledWorkExperiences>
        );
    }

    // 块级布局（每个任职一行）
    if (layout === 'block') {
        return (
            <StyledWorkExperiences className={`layout-block ${className}`}>
                {validExperiences.map((item, index) => (
                    <div key={index} className="work-experience-item">
                        {item}
                    </div>
                ))}
            </StyledWorkExperiences>
        );
    }

    // 行内布局（用顿号或换行符分隔）
    return (
        <StyledWorkExperiences as="span" className={`layout-inline ${className}`}>
            {validExperiences.map((item, index) => (
                <React.Fragment key={index}>
                    {item}
                    {index < validExperiences.length - 1 && <br />}
                </React.Fragment>
            ))}
        </StyledWorkExperiences>
    );
};

WorkExperiences.propTypes = {
    workExperiences: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            unitName: PropTypes.string,
            unitOffice: PropTypes.string,
            unitPost: PropTypes.string,
            unitJobTitle: PropTypes.string,
            memUnitId: PropTypes.number,
            organisation: PropTypes.shape({
                province: PropTypes.string,
                city: PropTypes.string,
            }),
        }),
    ),
    showSerialNumber: PropTypes.bool,
    showJobTitle: PropTypes.bool,
    separator: PropTypes.string,
    emptyText: PropTypes.string,
    layout: PropTypes.oneOf(['block', 'inline', 'orderedList']),
    className: PropTypes.string,
    customFormatter: PropTypes.func,
    formatterContext: PropTypes.any,
    showTooltipStyle: PropTypes.bool,
};

export default WorkExperiences;
